import { useRef } from 'react';

import { SubmitButton, Typography } from '@capsule/consumer-design-system';

import { useMountEffect } from '@react-hookz/web';
import type { CallHistoryMethodAction } from 'connected-react-router';
import { replace } from 'connected-react-router';
import { Form, Formik } from 'formik';
import type { FormikBag, FormikComputedProps, FormikState } from 'formik';
import styled, { css } from 'styled-components';
import * as Yup from 'yup';

import { Card, CardItem } from 'src/app/components/Elements/Card';
import LinkWithQuery from 'src/app/components/Elements/LinkWithQuery';
import StandardPage from 'src/app/components/Elements/Page/Standard';
import Spinner from 'src/app/components/Elements/Spinner';
import { FormikPasswordField } from 'src/app/components/Form/FormikPasswordField/FormikPasswordField';
import { FormikTextField } from 'src/app/components/Form/FormikTextField/FormikTextField';
import { useFocusOnFirstFormError } from 'src/app/components/Form/Hooks/useFocusOnFirstFormError';
import { useUserCheckPassword } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import { extractValidationErrors } from 'src/app/popsicle/utils';
import { checkout, hipaa, privacy, termsAndConditions } from 'src/app/router/paths';
import { bffFetchCart } from 'src/app/state/actions/cart';
import { getBFFCart, getIsBffCartFetching, getUserIsLoginSet } from 'src/app/state/selectors';
import { useDispatch, useSelector } from 'src/app/state/store';
import capsuleValidation from 'src/app/utils/capsuleValidation';

export interface FormValues {
  delivery_zip_code: string;
}

const PasswordFieldWrapper = styled.div`
  & #password-helper {
    white-space: normal !important;
    overflow: visible !important;
  }
`;

export interface AccountProps
  extends FormikBag<Record<string, string>, FormValues>,
    FormikComputedProps<FormValues>,
    FormikState<FormValues> {
  cartId: string;
  dispatchAccountSetup: ({ email, password }: { email: string; password: string }) => Promise<void>;
  dispatchPushNext: () => Promise<CallHistoryMethodAction<[string, any]>>;
}

const legaleseId = 'account-form-legalese';
const LegaleseContainer = styled(Typography).attrs({ id: legaleseId })`
  color: ${({ theme }) => theme.colors.capsuleGray50};
  margin-top: ${({ theme }) => theme.space.s4};
  margin-bottom: ${({ theme }) => theme.space.s4};

  & a {
    color: ${({ theme }) => theme.colors.capsuleGreen80};
  }
`;

const Account = ({
  isSubmitting,
  errors,
  isValidating,
  cartId,
  dispatchAccountSetup,
  dispatchPushNext,
}: AccountProps) => {
  const formRef = useRef(null);
  useFocusOnFirstFormError(formRef, errors, isSubmitting, isValidating, false);

  const { mutateAsync: checkPassword } = useUserCheckPassword();

  const bffCart = useSelector(getBFFCart);
  const isBFFCartFetching = useSelector(getIsBffCartFetching);
  const isLoginSet = useSelector(getUserIsLoginSet);

  const { login } = useAuth();

  const dispatch = useDispatch();
  const dispatchLoadCart = (cartId: string) => dispatch(bffFetchCart(cartId));
  const goToCheckout = () => dispatch(replace(checkout.toPath()));

  const initialValues = {
    email: '',
    password: '',
  };

  const schema = Yup.object().shape({
    email: capsuleValidation.email('Invalid email address.'),
    password: capsuleValidation.password(),
  });

  /**
   * We're unable to manipulate session history to remove the entry manually; however, If the user DOES use the back
   * button, this logic will send them back to the checkout page.
   */
  if (isLoginSet) goToCheckout();

  useMountEffect(() => {
    if (!bffCart && !isBFFCartFetching) {
      dispatchLoadCart(cartId);
    }
  });

  if (isBFFCartFetching || isSubmitting) {
    return (
      <StandardPage title={'Create an account'}>
        <Spinner centered />
      </StandardPage>
    );
  }

  return (
    <StandardPage title={'Create an account'}>
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={async ({ email, password }, { setSubmitting, setErrors, setFieldError }) => {
          setSubmitting(true);

          try {
            const passwordCheckResp = await checkPassword(password);
            if (!passwordCheckResp.isSafe) {
              setFieldError(
                'password',
                'This password is not secure or is too common. Try increasing length or adding numbers and symbols.'
              );
              setSubmitting(false);
              return;
            }
          } catch (error) {
            console.log(`Error checking password for email=${email}`);
          }

          try {
            await dispatchAccountSetup({ email: email, password: password });
            await login(email, password);
            dispatchPushNext();
          } catch (error) {
            setErrors(extractValidationErrors(error));
          }

          setSubmitting(false);
        }}
      >
        <Form aria-describedby={legaleseId}>
          <Card>
            <CardItem>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: ${({ theme }) => theme.space.s3};
                `}
              >
                <FormikTextField
                  name="email"
                  label={{ content: 'Email address', isHidden: false }}
                  type="email"
                  autoComplete="username"
                />

                <PasswordFieldWrapper>
                  <FormikPasswordField
                    name="password"
                    label={{ content: 'Password', isHidden: false }}
                    autoComplete="current-password"
                  />
                </PasswordFieldWrapper>
              </div>

              <SubmitButton
                variant="contained-secondary-lg"
                isSubmitting={isSubmitting}
                isFullWidth
                css={css`
                  margin-top: ${({ theme }) => theme.space.s6};
                `}
              >
                Save
              </SubmitButton>

              <LegaleseContainer>
                By clicking save, I accept the <LinkWithQuery to={termsAndConditions.toPath()}>Terms</LinkWithQuery>
                {', '}
                <LinkWithQuery to={privacy.toPath()}>Privacy</LinkWithQuery>
                {' and '}
                <LinkWithQuery to={hipaa.toPath()}>HIPAA</LinkWithQuery>
                {' Policies,'} and agree to receive account and delivery text messages. Msg frequency varies, msg and
                data rates may apply. Reply STOP to cancel.
              </LegaleseContainer>
            </CardItem>
          </Card>
        </Form>
      </Formik>
    </StandardPage>
  );
};

export default Account;
